import { message } from 'antd';
import { useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  platformUploadSizeLimitMap,
  platformUploadImageAcceptMap,
  supportSendVideoPlatforms,
  supportSendImagePlatforms,
} from 'services/config/platform';
import { acceptVideoTypes } from 'utils/constants';
import toLowerCase from 'utils/toLowerCase';
import validatorVideo from 'utils/validator/video';
import { useSetMediaPreview } from './useMediaPreview';
import { validatorLazadaImage } from 'utils/validator/lazadaImage';

const getFileType = (file: File) => {
  return file.type.split('/')[0];
};

const getFileFormat = (file: File) => {
  const { name } = file;

  const match = name.match(/(.*)\.(.*)$/);

  if (!match) {
    return '';
  }

  return match[2];
};

function useMediaFile(conversation?: Conversation.Conversation) {
  const { t } = useTranslate();

  const setMediaPreview = useSetMediaPreview();

  const fileManager = useCallback(
    (
      files: File[],
      onManageImage: (files: File[]) => void = (files) => {
        setMediaPreview({
          visible: true,
          type: 'image',
          files,
        });
      },
      onManageVideo: (files: File[]) => void = (files) => {
        setMediaPreview({
          visible: true,
          type: 'video',
          files,
        });
      },
    ) => {
      if (!conversation) {
        return;
      }

      const { channel } = conversation;

      const { imageFileList, videoFileList } = files.reduce(
        (res, file) => {
          const { imageFileList, videoFileList } = res;

          const fileType = getFileType(file);

          const fileFormat = getFileFormat(file);

          if (
            fileType === 'image' &&
            supportSendImagePlatforms.includes(channel) &&
            platformUploadImageAcceptMap[toLowerCase(channel)].includes(fileFormat)
          ) {
            imageFileList.push(file);
          }

          if (
            fileType === 'video' &&
            supportSendVideoPlatforms.includes(channel) &&
            acceptVideoTypes.includes(`.${fileFormat}`)
          ) {
            videoFileList.push(file);
          }

          return res;
        },
        {
          imageFileList: [] as File[],
          videoFileList: [] as File[],
        },
      );

      if (imageFileList.length > 0) {
        const { [toLowerCase(channel)]: uploadSizeLimit } = platformUploadSizeLimitMap;

        const sizeValidImageFileList = imageFileList.filter((file) => {
          return file.size <= uploadSizeLimit;
        });

        if (sizeValidImageFileList.length < imageFileList.length) {
          message.warn(
            t('upload.limitWarn', {
              size: `${(uploadSizeLimit / 1024 / 1024).toFixed(2)}M`,
            }),
          );
        }

        if (channel !== 'LAZADA') {
          if (sizeValidImageFileList.length > 0) {
            onManageImage(sizeValidImageFileList);
          }
        } else {
          sizeValidImageFileList
            .reduce<Promise<File[]>>((res, file) => {
              return res.then((res) => {
                return validatorLazadaImage(file).then((tips) => {
                  if (tips) {
                    const LAZADA_IMAGE_ERROR_MAP: Record<string, string> = {
                      'lazada-image-size-tips':
                        'scene.chatWorkspace.messageEditor.upload.error.lazadaImageSizeExceeded',
                    };
                    const key = LAZADA_IMAGE_ERROR_MAP[tips];
                    if (key) {
                      message.warn(t(key));
                    }
                  } else {
                    res.push(file);
                  }
                  return res;
                });
              });
            }, Promise.resolve([]))
            .then((files) => {
              if (files.length > 0) {
                onManageImage(files);
              }
            });
        }
      } else if (videoFileList.length > 0) {
        videoFileList
          .reduce<Promise<File[]>>((res, file) => {
            return res.then((list) => {
              return validatorVideo(channel, file, [0, 180])
                .then(({ errorText }) => {
                  if (errorText) {
                    message.warn(errorText);
                  } else {
                    list.push(file);
                  }

                  return list;
                })
                .catch(() => {
                  return list;
                });
            });
          }, Promise.resolve([]))
          .then((validVideoFileList) => {
            if (validVideoFileList.length > 1) {
              message.warn(
                t('scene.chatWorkspace.messageEditor.upload.validation.maxFileCount', {
                  maxFileCount: 1,
                }),
              );
            }

            const [firstFile] = validVideoFileList;

            if (firstFile) {
              onManageVideo([firstFile]);
            }
          });
      }
    },
    [conversation, t, setMediaPreview],
  );

  return fileManager;
}

export default useMediaFile;
